import type { FC } from 'react'

import styled from '@emotion/styled'

import Typography from '@segment/matcha/components/Typography'

import AccordionBase, * as Accordion from '@segment/matcha/recipes/Accordion'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import { useActiveState } from '@segment/utils/useActiveState'

const faqData = [
  {
    question: `What is identity resolution and why is it important?`,
    answer: `Identity resolution allows you to stitch together user behavior across platforms, anonymous or known state, or device, which allows you to see the entirety of a user’s journey and combine their customer data into a single golden profile. It is essential for understanding customer needs and preferences, as well as creating personalized customer journeys - especially in light of the ongoing legal changes around third-party cookie tracking.`,
  },
  {
    question: `How does Unify handle identity resolution?`,
    answer: `Unify resolves user identities in a deterministic manner. The Identity Graph supports multiple external IDs without any additional code. Each incoming event is analyzed, the external IDs are extracted, and then the Unify identity resolution tool algorithm matches events with profiles. A simplified version of the algorithm is as follows: Segment first searches the Identity Graph for incoming external IDs. If Segment doesn’t find a matching profile, a Profile is created for that user. If Segment finds one Profile, the event is logged onto that profile. If Segment finds multiple matching profiles, Segment applies the identity resolution settings to add the event to the appropriate profile.`,
  },
]

const copy = {
  heading: `Frequently asked questions`,
}

const FaqSection: FC = () => {
  const items = new Array(faqData.length).fill(false)
  const { activeItems, toggle } = useActiveState(items, {
    allowMultipleActive: true,
  })

  return (
    <StyledSectionBase variant="noCopy">
      <Section.Contents>
        <Heading variant="h2">{copy.heading}</Heading>
        <Accordion.JsonLdFaq
          data={faqData.map(({ answer, question }) => {
            return {
              question,
              answer,
            }
          })}
        />
        {activeItems.map((active, index) => (
          <AccordionBase key={`item-${index}`}>
            <Accordion.Header onClick={() => toggle(index)}>
              <Accordion.TitleWrapper>
                <Accordion.Title tag="h3" opened={active}>
                  {faqData[index].question}
                </Accordion.Title>
              </Accordion.TitleWrapper>
              <Accordion.Icon opened={active} />
            </Accordion.Header>
            <Accordion.Body>
              {active && (
                <Accordion.ContentWrapper>
                  <Accordion.Text>{faqData[index].answer}</Accordion.Text>
                </Accordion.ContentWrapper>
              )}
            </Accordion.Body>
          </AccordionBase>
        ))}
      </Section.Contents>
    </StyledSectionBase>
  )
}

const Heading = styled(Typography)`
  padding-bottom: 28px;
  text-align: center;
  margin-top: 0;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    padding-bottom: 44px;
  }
`

const StyledSectionBase = styled(SectionBase)`
  max-width: 1004px;
  overflow-x: unset;

  ${Section.Wrapper} {
    padding-top: 0;
  }

  ${({ theme }) => theme.breakpoints.down(`xs`)} {
    padding-bottom: 40px;
  }
`

export default FaqSection
